/**
 * Hero crop QA debug mode.
 *
 * Turns on a visual overlay that shows the live anchor point (`--hero-anchor-x/y`),
 * the zoom multiplier (`--hero-zoom`) and the crop bounds — i.e. which region of
 * the source image is actually visible inside the frame.
 *
 * Enable it any of these ways (all dev/QA-only, never on by default):
 *   • `?heroDebug=1` on any URL (`?heroDebug=0` turns it off again)
 *   • `Shift` + `H` anywhere on the page
 *   • `localStorage.heroDebug = "1"`
 */
import { useEffect, useState } from "react";

const KEY = "heroDebug";
const EVENT = "hero-debug:change";

function read(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const param = new URLSearchParams(window.location.search).get("heroDebug");
    if (param === "1" || param === "true") {
      window.localStorage.setItem(KEY, "1");
      return true;
    }
    if (param === "0" || param === "false") {
      window.localStorage.removeItem(KEY);
      return false;
    }
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function setHeroDebug(on: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (on) window.localStorage.setItem(KEY, "1");
    else window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: on }));
}

export function toggleHeroDebug() {
  setHeroDebug(!read());
}

/** Subscribes to the debug flag, including the Shift+H shortcut. */
export function useHeroDebug(): boolean {
  const [on, setOn] = useState(read);

  useEffect(() => {
    const sync = () => setOn(read());
    const onKey = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== "H" && e.key !== "h") return;
      const t = e.target as HTMLElement | null;
      if (t && /^(input|textarea|select)$/i.test(t.tagName)) return;
      if (t?.isContentEditable) return;
      toggleHeroDebug();
    };
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return on;
}

export interface HeroCropReadout {
  /** Resolved anchor as percentages of the frame. */
  anchorX: number;
  anchorY: number;
  /** Resolved zoom multiplier (CSS `--hero-zoom` × parallax scale). */
  scale: number;
  /** Frame size in CSS px. */
  frameW: number;
  frameH: number;
  /** Natural size of the decoded image. */
  naturalW: number;
  naturalH: number;
  /**
   * Visible region of the source image, in % of the source. `0,0,100,100`
   * means nothing is cropped away.
   */
  cropLeftPct: number;
  cropTopPct: number;
  cropWidthPct: number;
  cropHeightPct: number;
  /** Breakpoint label, useful when eyeballing screenshots. */
  breakpoint: string;
}

function parsePct(value: string, basis: number): number {
  if (value.endsWith("%")) return parseFloat(value);
  const px = parseFloat(value);
  return basis ? (px / basis) * 100 : 50;
}

function labelFor(width: number): string {
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  if (width < 1536) return "desktop";
  return "wide";
}

/**
 * Measures the effective crop of an `object-fit: cover` image so the overlay
 * can draw exactly which slice of the source survives the crop.
 */
export function measureHeroCrop(
  img: HTMLImageElement,
  frame: HTMLElement,
): HeroCropReadout | null {
  const naturalW = img.naturalWidth;
  const naturalH = img.naturalHeight;
  const frameRect = frame.getBoundingClientRect();
  const frameW = frameRect.width;
  const frameH = frameRect.height;
  if (!naturalW || !naturalH || !frameW || !frameH) return null;

  const cs = getComputedStyle(img);
  const [posXRaw, posYRaw] = cs.objectPosition.split(" ");
  const anchorX = parsePct(posXRaw ?? "50%", frameW);
  const anchorY = parsePct(posYRaw ?? posXRaw ?? "50%", frameH);

  // Effective uniform scale from the element transform matrix.
  let scale = 1;
  const m = cs.transform;
  if (m && m !== "none") {
    const nums = m
      .slice(m.indexOf("(") + 1, m.lastIndexOf(")"))
      .split(",")
      .map((n) => parseFloat(n));
    if (nums.length >= 6) scale = Math.hypot(nums[0], nums[1]) || 1;
    if (nums.length === 16) scale = Math.hypot(nums[0], nums[1]) || 1;
  }

  // object-fit: cover — the source is scaled so it covers the frame, then the
  // overflow is distributed according to object-position. The CSS transform
  // scale zooms the painted box on top of that, shrinking the visible slice.
  const coverScale = Math.max(frameW / naturalW, frameH / naturalH) * scale;
  const paintedW = naturalW * coverScale;
  const paintedH = naturalH * coverScale;

  const overflowX = Math.max(0, paintedW - frameW);
  const overflowY = Math.max(0, paintedH - frameH);
  const offsetX = overflowX * (anchorX / 100);
  const offsetY = overflowY * (anchorY / 100);

  return {
    anchorX,
    anchorY,
    scale,
    frameW,
    frameH,
    naturalW,
    naturalH,
    cropLeftPct: (offsetX / paintedW) * 100,
    cropTopPct: (offsetY / paintedH) * 100,
    cropWidthPct: Math.min(100, (frameW / paintedW) * 100),
    cropHeightPct: Math.min(100, (frameH / paintedH) * 100),
    breakpoint: labelFor(window.innerWidth),
  };
}
